/**
 * Content Review FACETS — the admin side of task-8xq63qswed (RULED by Jaume 2026-09-14: the issues the data
 * already counts must be filterable, and Select All must honour the filter).
 *
 * The backend declares the facets once (services/_contentReviewFacets.js) and every route reads them with the
 * same names; this module is the client mirror: the option lists the selects render, the subset of the queue
 * filters that IS the facet set (the exclusion the approve press carries), and the badge / count labels.
 *
 * ⛔ The facet values live INSIDE `QueueFilters`, never in component state of their own: the queue, Select All
 * and the facets endpoint all serialise them through `appendQueueFilterParams`, which is what makes the visible
 * set, the selected set and the counted set one set (the parity test pins that path).
 */

export type FacetValidation = 'passed' | 'failed' | 'unknown';
export type FacetGate = 'terminal' | 'clear';
export type FacetSpecRows = '4plus' | 'le3' | 'none';
export type FacetPreset = 'approvable';

export interface FacetFilters {
  validation?: FacetValidation | null;
  issue?: string | null;
  gate?: FacetGate | null;
  specRows?: FacetSpecRows | null;
  firstTime?: boolean | null;
  preset?: FacetPreset | null;
}

export type FacetDimension = 'validation' | 'issue' | 'gate' | 'specRows' | 'firstTime';
export const FACET_DIMENSIONS: FacetDimension[] = ['validation', 'issue', 'gate', 'specRows', 'firstTime'];

/** `all` is the select's "no constraint" sentinel; it never reaches the filters. */
export const ALL = 'all';

export const FACET_SELECTS: Array<{ key: FacetDimension; label: string; options: Array<{ value: string; label: string }> }> = [
  { key: 'validation', label: 'Validation', options: [
    { value: ALL, label: 'All validation' }, { value: 'passed', label: 'Validation passed' }, { value: 'failed', label: 'Validation failed' }, { value: 'unknown', label: 'Validation unknown' },
  ] },
  { key: 'issue', label: 'Hold reason', options: [
    { value: ALL, label: 'Any reason' }, { value: 'numeric_grounding_hold', label: 'Numeric grounding hold' },
  ] },
  { key: 'gate', label: 'Hard gate', options: [
    { value: ALL, label: 'All gate states' }, { value: 'clear', label: 'Gate clear' }, { value: 'terminal', label: 'Gate terminal' },
  ] },
  { key: 'specRows', label: 'Spec table', options: [
    { value: ALL, label: 'Any size' }, { value: '4plus', label: '4+ spec rows' }, { value: 'le3', label: '1–3 spec rows' }, { value: 'none', label: 'No spec table' },
  ] },
  { key: 'firstTime', label: 'History', options: [
    { value: ALL, label: 'All pages' }, { value: 'false', label: 'Previously published' }, { value: 'true', label: 'First-time page' },
  ] },
];

/** The select value for a dimension's current filter value (`all` when unset). */
export function facetSelectValue(filters: FacetFilters, key: FacetDimension): string {
  const v = filters[key];
  if (v === undefined || v === null) return ALL;
  return String(v);
}

/** The filter value for a select value (`all` → null; firstTime is a boolean on the wire). */
export function facetFromSelect(key: FacetDimension, value: string): FacetFilters[FacetDimension] {
  if (value === ALL) return null;
  if (key === 'firstTime') return value === 'true';
  return value as never;
}

/** The facet subset of the filters with the unset dimensions dropped — the exclusion the press carries. */
export function activeFacets(filters: FacetFilters): FacetFilters {
  const out: FacetFilters = {};
  for (const key of FACET_DIMENSIONS) {
    const v = filters[key];
    if (v !== undefined && v !== null && v !== '') (out as Record<string, unknown>)[key] = v;
  }
  if (filters.preset) out.preset = filters.preset;
  return out;
}

export function facetsAreActive(filters: FacetFilters): boolean {
  return Object.keys(activeFacets(filters)).length > 0;
}

/** Badge labels for the active facets, in a fixed order, the preset first. */
export function facetBadges(filters: FacetFilters): string[] {
  const out: string[] = [];
  if (filters.preset === 'approvable') out.push('Approvable preset');
  for (const sel of FACET_SELECTS) {
    const v = facetSelectValue(filters, sel.key);
    if (v === ALL) continue;
    const opt = sel.options.find((o) => o.value === v);
    out.push(opt ? opt.label : `${sel.label}: ${v}`);
  }
  return out;
}

export interface FacetCounts {
  total: number;
  matching: number;
  approvable: number;
  /** Non-product rows (articles, category pages) the queue retains under any facet — counted in the three totals. */
  nonProduct?: number;
  counts: Partial<Record<FacetDimension, Record<string, number>>>;
  truncated?: boolean;
}

/** An option label with its count under the OTHER active facets, when the counts are loaded. */
export function labelWithCount(counts: FacetCounts | null, key: FacetDimension, value: string, label: string): string {
  if (!counts || value === ALL) return label;
  const n = counts.counts?.[key]?.[value];
  return n === undefined ? `${label} (0)` : `${label} (${n})`;
}
